import type { FunctionComponent } from "react";
import { 
    Box, 
    Typography, 
    Container, 
    Paper,
    useTheme,
    alpha,
    LinearProgress,
    Chip,
    Stack,
    Button,
    Divider,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { 
    RiToolsLine, 
    RiTimeLine, 
    RiMailLine, 
    RiPhoneLine,
    RiCalendarLine,
    RiInformationLine,
    RiArrowDownSLine,
    RiRefreshLine
} from "@remixicon/react";
import { useMaintenanceMode } from "../contexts/MaintenanceContext";
import { useEffect, useState } from "react";

interface AdvancedMaintenanceModeProps {
  allowRefresh?: boolean;
  showScheduleInfo?: boolean;
  showMaintenanceType?: boolean;
  customActions?: React.ReactNode;
}

const AdvancedMaintenanceMode: FunctionComponent<AdvancedMaintenanceModeProps> = ({
  allowRefresh = true,
  showScheduleInfo = true,
  showMaintenanceType = true,
  customActions
}) => {
  const theme = useTheme();
  const { config, checkMaintenanceStatus } = useMaintenanceMode();
  const [lastChecked, setLastChecked] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Función para formatear fecha y hora
  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return 'No especificado';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('es-DO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener el color del chip según el tipo de mantenimiento
  const getMaintenanceTypeColor = (type: string) => {
    switch (type) {
      case 'emergency':
        return { 
          bg: alpha(theme.palette.error.main, 0.1),
          border: alpha(theme.palette.error.main, 0.3),
          color: theme.palette.error.dark
        };
      case 'update':
        return { 
          bg: alpha(theme.palette.info.main, 0.1),
          border: alpha(theme.palette.info.main, 0.3),
          color: theme.palette.info.dark
        };
      default: // scheduled
        return { 
          bg: alpha(theme.palette.warning.main, 0.1),
          border: alpha(theme.palette.warning.main, 0.3),
          color: theme.palette.warning.dark
        };
    }
  };

  // Obtener etiqueta del tipo de mantenimiento
  const getMaintenanceTypeLabel = (type: string) => {
    switch (type) {
      case 'emergency': return 'Mantenimiento de Emergencia';
      case 'update': return 'Actualización del Sistema';
      default: return 'Mantenimiento Programado';
    }
  };

  // Manejar actualización de estado
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await checkMaintenanceStatus();
      setLastChecked(new Date());
    } catch (error) {
      console.error('Error refreshing maintenance status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Actualizar última verificación cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setLastChecked(new Date());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const maintenanceColors = getMaintenanceTypeColor(config.maintenanceType);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
      }}
    >
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            padding: { xs: 4, md: 6 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.1),
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Estado y tipo de mantenimiento */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="center"
            sx={{ mb: 3 }}
          >
            <Chip
              label="Sistema no disponible"
              sx={{
                backgroundColor: maintenanceColors.bg,
                color: maintenanceColors.color,
                fontWeight: 600,
                fontSize: '0.875rem',
                border: '1px solid',
                borderColor: maintenanceColors.border,
              }}
            />
            
            {showMaintenanceType && (
              <Chip
                label={getMaintenanceTypeLabel(config.maintenanceType)}
                variant="outlined"
                sx={{
                  fontWeight: 500,
                  borderColor: alpha(theme.palette.grey[500], 0.3),
                  color: theme.palette.grey[700],
                }}
              />
            )}
          </Stack>

          {/* Icono principal con animación */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 3,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 140,
                height: 140,
                borderRadius: '50%',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <RiToolsLine
                size={70}
                color={theme.palette.primary.main}
                style={{
                  animation: 'rotate 4s linear infinite',
                }}
              />
            </Box>
            <style>
              {`
                @keyframes rotate {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes pulse {
                  0%, 100% { opacity: 0.6; }
                  50% { opacity: 1; }
                }
              `}
            </style>
          </Box>

          {/* Título principal */}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              mb: 2,
              fontFamily: 'Montserrat',
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            {config.title}
          </Typography>

          {/* Mensaje principal */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.grey[700],
              mb: 4,
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.7,
              fontSize: '1.1rem',
            }}
          >
            {config.message}
          </Typography>

          {/* Alerta de información importante */}
          {config.maintenanceType === 'emergency' && (
            <Alert 
              severity="warning" 
              sx={{ mb: 4, textAlign: 'left' }}
              icon={<RiInformationLine size={20} />}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Mantenimiento de emergencia en curso. Se requiere atención inmediata para garantizar la estabilidad del sistema.
              </Typography>
            </Alert>
          )}

          {/* Información de tiempo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              mb: 4,
              p: 2,
              backgroundColor: alpha(theme.palette.info.main, 0.05),
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          >
            <RiTimeLine
              size={20}
              color={theme.palette.info.main}
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.info.dark,
                fontWeight: 600,
              }}
            >
              Duración estimada: {config.estimatedTime}
            </Typography>
          </Box>

          {/* Información de horario (si está disponible) */}
          {showScheduleInfo && (config.startTime || config.endTime) && (
            <Accordion sx={{ mb: 4 }}>
              <AccordionSummary
                expandIcon={<RiArrowDownSLine size={20} />}
                aria-controls="schedule-content"
                id="schedule-header"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RiCalendarLine size={18} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Información de horario
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1} sx={{ textAlign: 'left' }}>
                  <Typography variant="body2">
                    <strong>Inicio:</strong> {formatDateTime(config.startTime)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Finalización estimada:</strong> {formatDateTime(config.endTime)}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Barra de progreso */}
          {config.showProgress && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.grey[600],
                  mb: 1,
                }}
              >
                Aplicando mejoras al sistema...
              </Typography>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                    animation: 'pulse 2s ease-in-out infinite',
                  },
                }}
              />
            </Box>
          )}

          <Divider sx={{ my: 4 }} />

          {/* Botón de actualización */}
          {allowRefresh && (
            <Box sx={{ mb: 4 }}>
              <Button
                variant="outlined"
                startIcon={<RiRefreshLine size={18} />}
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  paddingX: 3,
                  paddingY: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                {isRefreshing ? 'Verificando...' : 'Verificar estado'}
              </Button>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  color: theme.palette.grey[500],
                }}
              >
                Última verificación: {lastChecked.toLocaleTimeString('es-DO')}
              </Typography>
            </Box>
          )}

          {/* Acciones personalizadas */}
          {customActions && (
            <Box sx={{ mb: 4 }}>
              {customActions}
            </Box>
          )}

          {/* Información de contacto */}
          {config.showContactInfo && (
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.grey[800],
                  mb: 2,
                }}
              >
                ¿Necesitas asistencia urgente?
              </Typography>
              
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 4 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    backgroundColor: alpha(theme.palette.grey[500], 0.05),
                    borderRadius: 2,
                    minWidth: 250,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.grey[500], 0.1),
                  }}
                >
                  <RiMailLine
                    size={20}
                    color={theme.palette.grey[600]}
                  />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.grey[500],
                        display: 'block',
                      }}
                    >
                      Correo electrónico
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[700],
                        fontWeight: 500,
                      }}
                    >
                      {config.contactEmail}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    backgroundColor: alpha(theme.palette.grey[500], 0.05),
                    borderRadius: 2,
                    minWidth: 250,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.grey[500], 0.1),
                  }}
                >
                  <RiPhoneLine
                    size={20}
                    color={theme.palette.grey[600]}
                  />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.grey[500],
                        display: 'block',
                      }}
                    >
                      Línea directa
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.grey[700],
                        fontWeight: 500,
                      }}
                    >
                      {config.contactPhone}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Footer con información del PJ */}
          <Box
            sx={{
              pt: 3,
              borderTop: '1px solid',
              borderColor: alpha(theme.palette.grey[500], 0.2),
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.grey[500],
                fontWeight: 500,
                mb: 1,
              }}
            >
              Portal de Acceso Digital - Poder Judicial de la República Dominicana
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.grey[400],
              }}
            >
              Gracias por tu comprensión mientras mejoramos nuestros servicios
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdvancedMaintenanceMode;
